import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  createAdminRoomPrompt,
  deactivateAdminRoomPrompt,
  deleteAdminRoomPrompt,
  getAdminRoomPrompt,
  globalSearch,
  listAdminRoomPrompts,
  reactivateAdminRoomPrompt,
  updateAdminRoomPrompt,
} from "../api";
import { getLeagueCodeFromAny, getLeagueNameFromAny } from "../utils/leagueRouting";

const DEFAULT_BORDER_COLOR = "rgba(148,163,184,0.24)";

const cardStyle = {
  border: "1px solid rgba(148,163,184,0.2)",
  background: "linear-gradient(180deg, rgba(8,20,17,0.96), rgba(6,13,12,0.96))",
  borderRadius: 14,
  padding: 14,
  boxShadow: "0 10px 24px rgba(0,0,0,0.22)",
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  borderRadius: 8,
  border: `1px solid ${DEFAULT_BORDER_COLOR}`,
  borderColor: DEFAULT_BORDER_COLOR,
  background: "rgba(255,255,255,0.04)",
  color: "#f8fafc",
  padding: "10px 12px",
  fontSize: 14,
};

const labelStyle = {
  display: "grid",
  gap: 6,
  fontSize: 13,
  color: "#cbd5e1",
};

const helperStyle = {
  fontSize: 12,
  color: "#94a3b8",
};

const errorStyle = {
  fontSize: 12,
  color: "#fca5a5",
};

const promptTypes = ["player_award", "poll", "moment", "discussion"];

const emptyOption = () => ({
  option_uid: `manual-${Math.random().toString(36).slice(2, 10)}`,
  label: "",
  player_id: "",
  player_name: "",
  team_id: "",
  metaText: "",
  source: "manual",
});

const createPresetOptions = (labels) =>
  labels.map((label) => ({
    ...emptyOption(),
    label,
  }));

const emptyForm = {
  team_name: "",
  sport: "",
  league_key: "",
  league_name: "",
  season: "",
  league_id_helper: "",
  prompt_type: "player_award",
  question: "",
  duration_hours: 168,
  team_id_helper: "",
  options: [emptyOption(), emptyOption()],
};

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const safeNum = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const safeTrim = (value) => String(value || "").trim();

const normalise = (value) => String(value || "").trim().toLowerCase();

const stringifyMeta = (meta) => {
  if (!meta || typeof meta !== "object" || !Object.keys(meta).length) return "";
  try {
    return JSON.stringify(meta, null, 2);
  } catch {
    return "";
  }
};

const parseMetaText = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return {};
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return null;
  }
};

const formatDuration = (hours) => {
  const total = Number(hours || 0);
  if (!total || total < 0) return "";
  const days = Math.floor(total / 24);
  if (days >= 1) return `${days}d left`;
  return `${total}h left`;
};

const formatTimestamp = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const modalBackdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(2,6,23,0.72)",
  display: "grid",
  placeItems: "center",
  padding: 20,
  zIndex: 90,
};

const modalCardStyle = {
  ...cardStyle,
  width: "min(960px, 100%)",
  maxHeight: "90vh",
  overflowY: "auto",
};

const getDefaultFootballSeason = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  return month >= 6 ? year : year - 1;
};

const formatPromptScope = (prompt = {}) =>
  safeTrim(prompt?.league_key) ? "Competition-specific prompt" : "Club-wide prompt";

const durationHoursFromPrompt = (prompt = {}) => {
  const start = prompt?.created_at ? new Date(prompt.created_at) : null;
  const end = prompt?.expires_at ? new Date(prompt.expires_at) : null;
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 168;
  const hours = Math.round((end.getTime() - start.getTime()) / 36e5);
  return hours > 0 ? hours : 168;
};

const inferSport = (team = {}) => {
  const raw = firstDefined(team?.sport, team?.sport_key, team?.sport_name, "football");
  return String(raw || "football").trim().toLowerCase();
};

const inferSportFromLeague = (leagueCode, leagueName, fallbackSport = "football") => {
  const code = safeTrim(leagueCode).toUpperCase();
  const name = safeTrim(leagueName).toUpperCase();
  if (code === "NBA" || name.includes("NBA")) return "basketball";
  if (code === "MLB" || name.includes("MLB") || name.includes("MAJOR LEAGUE BASEBALL")) return "baseball";
  if (code === "NFL" || name.includes("NFL")) return "american_football";
  if (code === "NHL" || name.includes("NHL")) return "hockey";
  if (name.includes("TENNIS")) return "tennis";
  if (name.includes("GOLF")) return "golf";
  if (name.includes("RUGBY")) return "rugby";
  return safeTrim(fallbackSport).toLowerCase() || "football";
};

const resolveTeamLeagueContext = (team = {}, fallback = {}) => {
  const leagueId = firstDefined(
    team?.league_id,
    team?.competition_id,
    team?.comp_id,
    team?.league?.id,
    team?.competition?.id,
    fallback?.league_id
  );
  const rawLeagueName = firstDefined(
    team?.league_name,
    team?.league,
    team?.competition_name,
    team?.competition,
    team?.comp_name,
    team?.league?.name,
    team?.competition?.name,
    fallback?.league_name
  );
  const rawLeagueCode = firstDefined(
    team?.league_code,
    team?.comp_code,
    team?.competition_code,
    team?.league?.code,
    team?.competition?.code,
    fallback?.league_key
  );
  const league_key = getLeagueCodeFromAny({ code: rawLeagueCode, name: rawLeagueName, id: leagueId }) || "";
  const league_name = getLeagueNameFromAny({ code: league_key || rawLeagueCode, name: rawLeagueName, id: leagueId }) || safeTrim(rawLeagueName);
  return {
    league_id: safeNum(leagueId),
    league_key,
    league_name,
    unresolved: !league_key,
  };
};

const presetConfig = {
  "Player of the Year": {
    prompt_type: "player_award",
    question: "Who is your player of the year?",
    duration_hours: 168,
    options: [emptyOption(), emptyOption()],
  },
  "Dream Transfer": {
    prompt_type: "poll",
    question: "Who is the dream transfer for this team?",
    duration_hours: 168,
    options: createPresetOptions([
      "A marquee striker",
      "Creative midfielder",
      "Dominant centre-back",
      "Game-breaking winger",
    ]),
  },
  "Moment of the Year": {
    prompt_type: "moment",
    question: "What has been the moment of the year so far?",
    duration_hours: 168,
    options: createPresetOptions([
      "Statement win",
      "Derby day drama",
      "Breakout performance",
      "Late comeback",
    ]),
  },
  "Next Captain": {
    prompt_type: "poll",
    question: "Who should be the next captain?",
    duration_hours: 168,
    options: createPresetOptions([
      "Current leadership core",
      "Young star stepping up",
      "New voice in the squad",
      "Need more time to decide",
    ]),
  },
};

const collectPlayerLikeRows = (value, depth = 0, seen = new Set()) => {
  if (!value || depth > 5) return [];
  if (typeof value !== "object") return [];
  if (seen.has(value)) return [];
  seen.add(value);

  if (Array.isArray(value)) {
    const looksPlayerish = value.some((entry) => {
      const row = entry?.player || entry;
      return Boolean(
        row?.name ||
        entry?.player_name ||
        row?.photo ||
        entry?.player_id ||
        entry?.statistics ||
        entry?.games ||
        entry?.avg_rating ||
        entry?.average_rating
      );
    });
    if (looksPlayerish) return value;
    return value.flatMap((entry) => collectPlayerLikeRows(entry, depth + 1, seen));
  }

  return Object.values(value).flatMap((entry) => collectPlayerLikeRows(entry, depth + 1, seen));
};

const extractPlayerCandidates = (payload, fallbackTeamId) => {
  const directArrays = [
    payload?.players,
    payload?.squad,
    payload?.roster,
    payload?.top_players,
    payload?.season_players,
    payload?.team?.players,
    payload?.team?.squad,
    payload?.team?.roster,
    payload?.overview?.players,
    payload?.overview?.squad,
    payload?.overview?.roster,
    payload?.response?.players,
    payload?.response?.squad,
    payload?.response?.roster,
  ];

  const directRows = directArrays.find((value) => Array.isArray(value) && value.length) || [];
  const rows = directRows.length ? directRows : collectPlayerLikeRows(payload);

  return rows
    .map((entry) => {
      const player = entry?.player || entry;
      const stat = Array.isArray(entry?.statistics) ? entry.statistics[0] || {} : entry?.statistics || entry?.stats || {};
      const games = stat?.games || entry?.games || {};
      const rating = safeNum(
        firstDefined(games?.rating, entry?.rating, entry?.avg_rating, entry?.average_rating, stat?.rating)
      );
      const apps = safeNum(
        firstDefined(games?.appearences, games?.appearances, entry?.apps, entry?.appearances, entry?.matches)
      );
      const playerId = safeNum(firstDefined(player?.id, entry?.player_id));
      const teamId = safeNum(firstDefined(stat?.team?.id, entry?.team_id, player?.team_id, fallbackTeamId));
      const name = firstDefined(player?.name, entry?.player_name, entry?.name, "");
      if (!name) return null;
      return {
        key: String(playerId || `${name}-${teamId || "team"}`),
        label: name,
        player_id: playerId,
        player_name: name,
        team_id: teamId,
        meta: {
          photo: firstDefined(player?.photo, entry?.photo, null),
          rating,
          apps,
          position: firstDefined(games?.position, entry?.position, player?.position, null),
        },
        source: "squad",
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const ratingA = Number(a.meta?.rating ?? -1);
      const ratingB = Number(b.meta?.rating ?? -1);
      if (ratingB !== ratingA) return ratingB - ratingA;
      const appsA = Number(a.meta?.apps ?? -1);
      const appsB = Number(b.meta?.apps ?? -1);
      return appsB - appsA;
    });
};

const buildPreviewPercentages = (options) => {
  const valid = (Array.isArray(options) ? options : []).filter((option) => safeTrim(option?.label));
  if (!valid.length) return [];
  const weights = valid.map((option, idx) => {
    const meta = option.meta && typeof option.meta === "object" ? option.meta : {};
    const base = 100 - idx * 6;
    const rating = Number(meta.rating || 0) * 8;
    const apps = Number(meta.apps || 0) * 0.6;
    return Math.max(8, base + rating + apps);
  });
  const total = weights.reduce((sum, value) => sum + value, 0) || 1;
  return weights.map((value) => Math.round((value / total) * 100));
};

const validationErrors = (form) => {
  const errors = {};
  if (!safeTrim(form?.team_name)) errors.team_name = "Choose a club to begin.";
  if (!safeTrim(form?.sport)) errors.sport = "Sport is required.";
  if (!safeTrim(form?.question)) errors.question = "Question is required.";
  if (Number(form?.duration_hours) <= 0) errors.duration_hours = "Use a positive duration.";
  const options = Array.isArray(form?.options) ? form.options : [];
  const validOptions = options.filter((option) => safeTrim(option?.label));
  if (validOptions.length < 2) errors.options = "Add at least two answer options.";
  const badMeta = options.find((option) => safeTrim(option?.label) && parseMetaText(option?.metaText) === null);
  if (badMeta) errors.meta = `Meta JSON is invalid for "${badMeta.label}".`;
  return errors;
};

function TeamSearchResult({ team, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(team)}
      style={{
        width: "100%",
        textAlign: "left",
        border: "none",
        background: "transparent",
        color: "#f8fafc",
        padding: "10px 12px",
        cursor: "pointer",
        display: "grid",
        gap: 4,
      }}
    >
      <div style={{ fontWeight: 800 }}>{firstDefined(team?.name, team?.team_name, "Club")}</div>
      <div style={{ fontSize: 12, color: "#94a3b8" }}>
        {firstDefined(team?.league_name, team?.league, team?.competition_name, "League unknown")}
        {team?.league_code || team?.comp_code ? ` • ${firstDefined(team?.league_code, team?.comp_code)}` : ""}
      </div>
    </button>
  );
}

function OptionCard({ option, index, total, onChange, onRemove, onMove }) {
  const meta = parseMetaText(option.metaText);
  const photo = meta && typeof meta === "object" ? meta.photo : null;
  const statLine =
    meta && typeof meta === "object" && (meta.rating != null || meta.apps != null || meta.position)
      ? `Avg rating ${meta.rating != null ? Number(meta.rating).toFixed(1) : "—"} • ${meta.apps ?? "—"} apps${meta.position ? ` • ${meta.position}` : ""}`
      : "";

  return (
    <div
      style={{
        border: "1px solid rgba(148,163,184,0.16)",
        borderRadius: 12,
        padding: 12,
        background: "rgba(255,255,255,0.03)",
        display: "grid",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
          {photo ? (
            <img src={photo} alt="" style={{ width: 42, height: 42, borderRadius: 999, objectFit: "cover" }} />
          ) : (
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 999,
                display: "grid",
                placeItems: "center",
                background: "rgba(255,255,255,0.08)",
                color: "#f8fafc",
                fontWeight: 900,
              }}
            >
              {(option.label || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ color: "#f8fafc", fontWeight: 800 }}>{option.label || `Option ${index + 1}`}</div>
            <div style={{ color: "#94a3b8", fontSize: 12 }}>
              {option.source === "squad" ? "Generated from squad" : "Custom option"}
            </div>
            {statLine ? <div style={{ color: "#86efac", fontSize: 12, marginTop: 2 }}>{statLine}</div> : null}
          </div>
        </div>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button type="button" disabled={index === 0} onClick={() => onMove(index, -1)} style={{ ...inputStyle, width: "auto", padding: "6px 10px", cursor: "pointer" }}>
            ↑
          </button>
          <button type="button" disabled={index === total - 1} onClick={() => onMove(index, 1)} style={{ ...inputStyle, width: "auto", padding: "6px 10px", cursor: "pointer" }}>
            ↓
          </button>
          {total > 2 ? (
            <button type="button" onClick={() => onRemove(index)} style={{ ...inputStyle, width: "auto", padding: "6px 10px", cursor: "pointer", color: "#fca5a5" }}>
              Remove
            </button>
          ) : null}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
        <label style={labelStyle}>
          <span>Label</span>
          <input value={option.label} onChange={(e) => onChange(index, "label", e.target.value)} style={inputStyle} />
        </label>
        <label style={labelStyle}>
          <span>Player name</span>
          <input value={option.player_name} onChange={(e) => onChange(index, "player_name", e.target.value)} style={inputStyle} />
        </label>
        <label style={labelStyle}>
          <span>Player ID</span>
          <input value={option.player_id} onChange={(e) => onChange(index, "player_id", e.target.value)} style={inputStyle} />
        </label>
        <label style={labelStyle}>
          <span>Team ID</span>
          <input value={option.team_id} onChange={(e) => onChange(index, "team_id", e.target.value)} style={inputStyle} />
        </label>
      </div>

      <label style={labelStyle}>
        <span>Meta (JSON)</span>
        <textarea
          rows={4}
          value={option.metaText}
          onChange={(e) => onChange(index, "metaText", e.target.value)}
          placeholder='{"photo":"https://...","rating":7.62,"apps":31,"position":"F"}'
          style={{
            ...inputStyle,
            resize: "vertical",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 12,
          }}
        />
      </label>
    </div>
  );
}

function PromptStatusBadge({ active }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        borderRadius: 999,
        padding: "4px 9px",
        fontSize: 12,
        fontWeight: 800,
        color: active === false ? "#fca5a5" : "#bbf7d0",
        background: active === false ? "rgba(239,68,68,0.12)" : "rgba(34,197,94,0.12)",
        border: active === false ? "1px solid rgba(239,68,68,0.28)" : "1px solid rgba(34,197,94,0.28)",
      }}
    >
      {active === false ? "Inactive" : "Active"}
    </span>
  );
}

function ConfirmPromptActionModal({ open, title, description, confirmLabel, confirmTone = "danger", onCancel, onConfirm, busy }) {
  if (!open) return null;
  return (
    <div style={modalBackdropStyle}>
      <div style={{ ...modalCardStyle, width: "min(520px, 100%)", display: "grid", gap: 12 }}>
        <div style={{ color: "#f8fafc", fontSize: 20, fontWeight: 900 }}>{title}</div>
        <div style={{ color: "#cbd5e1", fontSize: 14, lineHeight: 1.45 }}>{description}</div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" onClick={onCancel} style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer" }}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            style={{
              ...inputStyle,
              width: "auto",
              padding: "8px 12px",
              cursor: busy ? "not-allowed" : "pointer",
              color: confirmTone === "danger" ? "#fecaca" : "#dcfce7",
              borderColor: confirmTone === "danger" ? "rgba(239,68,68,0.32)" : "rgba(34,197,94,0.32)",
              background: confirmTone === "danger" ? "rgba(239,68,68,0.12)" : "rgba(34,197,94,0.14)",
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? "Working..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminRoomQuestionsPanel() {
  const manualIdRef = useRef(1);
  const submitLockRef = useRef(false);
  const [form, setForm] = useState(emptyForm);
  const [teamQuery, setTeamQuery] = useState("");
  const [teamResults, setTeamResults] = useState([]);
  const [teamSearchLoading, setTeamSearchLoading] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [squadCandidates, setSquadCandidates] = useState([]);
  const [selectedPlayerKeys, setSelectedPlayerKeys] = useState([]);
  const [squadLoading, setSquadLoading] = useState(false);
  const [squadError, setSquadError] = useState("");
  const [squadReloadTick, setSquadReloadTick] = useState(0);
  const [pendingPlayerAwardAutofill, setPendingPlayerAwardAutofill] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [responseData, setResponseData] = useState(null);
  const [topError, setTopError] = useState("");
  const [showCompetitionFields, setShowCompetitionFields] = useState(false);
  const [promptListLoading, setPromptListLoading] = useState(false);
  const [promptListError, setPromptListError] = useState("");
  const [existingPrompts, setExistingPrompts] = useState([]);
  const [promptListReloadTick, setPromptListReloadTick] = useState(0);
  const [managementFilters, setManagementFilters] = useState({
    team_name: "",
    sport: "",
    prompt_type: "",
    league_key: "",
    active_only: "false",
    limit: "50",
  });
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [promptDetail, setPromptDetail] = useState(null);
  const [promptDetailLoading, setPromptDetailLoading] = useState(false);
  const [promptDetailError, setPromptDetailError] = useState("");
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const makeManualOption = useCallback(
    (partial = {}) => ({
      ...emptyOption(),
      option_uid: `manual-${manualIdRef.current++}`,
      ...partial,
    }),
    []
  );

  const normalisePromptOption = useCallback(
    (option = {}) =>
      makeManualOption({
        option_uid: option?.player_id ? `player-${option.player_id}` : option?.id ? `option-${option.id}` : undefined,
        label: safeTrim(option?.label),
        player_id: option?.player_id ? String(option.player_id) : "",
        player_name: safeTrim(option?.player_name),
        team_id: option?.team_id ? String(option.team_id) : "",
        metaText: stringifyMeta(option?.meta),
        source: option?.player_id ? "squad" : "manual",
      }),
    [makeManualOption]
  );

  const loadPromptIntoCreateForm = useCallback(
    (prompt = {}) => {
      const options = Array.isArray(prompt?.options) && prompt.options.length
        ? prompt.options.map(normalisePromptOption)
        : [makeManualOption(), makeManualOption()];
      setForm({
        team_name: safeTrim(prompt?.team_name),
        sport: safeTrim(prompt?.sport),
        league_key: safeTrim(prompt?.league_key),
        league_name: safeTrim(prompt?.league_name),
        season: prompt?.season ? String(prompt.season) : String(getDefaultFootballSeason()),
        league_id_helper: prompt?.league_id ? String(prompt.league_id) : "",
        prompt_type: safeTrim(prompt?.prompt_type) || "player_award",
        question: safeTrim(prompt?.question),
        duration_hours: durationHoursFromPrompt(prompt),
        team_id_helper: prompt?.team_id ? String(prompt.team_id) : "",
        options,
      });
      setTeamQuery(safeTrim(prompt?.team_name));
      setManagementFilters((prev) => ({
        ...prev,
        team_name: safeTrim(prompt?.team_name),
        sport: safeTrim(prompt?.sport),
        league_key: safeTrim(prompt?.league_key),
      }));
      setSuccess("");
      setResponseData(null);
      setPendingPlayerAwardAutofill(false);
    },
    [makeManualOption, normalisePromptOption]
  );

  useEffect(() => {
    const q = teamQuery.trim();
    if (!q || selectedTeam?.query === q) {
      if (!q) setTeamResults([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setTeamSearchLoading(true);
      try {
        const data = await globalSearch(q, 6);
        if (cancelled) return;
        setTeamResults(Array.isArray(data?.teams) ? data.teams : []);
      } catch {
        if (!cancelled) setTeamResults([]);
      } finally {
        if (!cancelled) setTeamSearchLoading(false);
      }
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [selectedTeam, teamQuery]);

  useEffect(() => {
    const isFootball = normalise(form.sport) === "football" || normalise(form.sport) === "soccer";
    const teamId = safeNum(form.team_id_helper);
    const leagueId = safeNum(form.league_id_helper || selectedTeam?.league_id || selectedTeam?.competition_id || selectedTeam?.comp_id);
    const season = safeNum(form.season) || getDefaultFootballSeason();
    if (!isFootball) {
      setSquadCandidates([]);
      setSelectedPlayerKeys([]);
      setSquadError("");
      return undefined;
    }
    if (!teamId) {
      setSquadCandidates([]);
      setSelectedPlayerKeys([]);
      setSquadError("Team id missing.");
      return undefined;
    }
    if (!season) {
      setSquadCandidates([]);
      setSelectedPlayerKeys([]);
      setSquadError("Season missing for squad lookup.");
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setSquadLoading(true);
      setSquadError("");
      try {
        const params = {
          team_id: teamId,
          season,
          ...(leagueId ? { league_id: leagueId } : {}),
        };
        const { data } = await api.get("/football/club/squad", { params });
        if (cancelled) return;
        const rows = extractPlayerCandidates(data, teamId);
        setSquadCandidates(rows);
        setSelectedPlayerKeys(rows.slice(0, 4).map((row) => row.key));
        if (!rows.length) {
          setSquadError("Could not load squad data for this club.");
        }
      } catch (err) {
        if (!cancelled) {
          setSquadCandidates([]);
          setSelectedPlayerKeys([]);
          setSquadError(err?.response?.data?.detail || "Could not load squad data for this club.");
        }
      } finally {
        if (!cancelled) setSquadLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.league_id_helper, form.season, form.sport, form.team_id_helper, selectedTeam, squadReloadTick]);

  useEffect(() => {
    const teamName = safeTrim(managementFilters.team_name);
    const sport = safeTrim(managementFilters.sport);
    const promptType = safeTrim(managementFilters.prompt_type);
    const leagueKey = safeTrim(managementFilters.league_key);
    const activeOnly = String(managementFilters.active_only) === "true";
    const limit = Number(managementFilters.limit) || 50;
    if (!teamName && !sport && !promptType && !leagueKey) {
      setExistingPrompts([]);
      setPromptListError("");
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setPromptListLoading(true);
      setPromptListError("");
      try {
        const data = await listAdminRoomPrompts({
          ...(teamName ? { team_name: teamName } : {}),
          ...(sport ? { sport } : {}),
          ...(promptType ? { prompt_type: promptType } : {}),
          ...(leagueKey ? { league_key: leagueKey } : {}),
          active_only: activeOnly,
          limit,
        });
        if (cancelled) return;
        const rows = Array.isArray(data?.prompts)
          ? data.prompts
          : Array.isArray(data)
          ? data
          : [];
        setExistingPrompts(
          [...rows].sort(
            (a, b) =>
              new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime()
          )
        );
      } catch (err) {
        if (!cancelled) {
          setExistingPrompts([]);
          setPromptListError(err?.response?.data?.detail || "Could not load existing prompts for this club.");
        }
      } finally {
        if (!cancelled) setPromptListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [managementFilters, promptListReloadTick, success]);

  const previewOptions = useMemo(
    () =>
      form.options.map((option) => ({
        ...option,
        meta: parseMetaText(option.metaText),
      })),
    [form.options]
  );

  const errors = useMemo(() => validationErrors(form), [form]);
  const canPublish = Object.keys(errors).length === 0 && !submitting;
  const previewPercents = useMemo(() => buildPreviewPercentages(previewOptions), [previewOptions]);
  const suggestedCompetition = useMemo(
    () => (selectedTeam ? resolveTeamLeagueContext(selectedTeam, form) : { league_key: "", league_name: "", unresolved: true }),
    [selectedTeam, form]
  );
  const missingLeagueKeyWarning = safeTrim(form.team_name) && !safeTrim(form.league_key);

  const updateField = (key, value) => {
    if (key === "prompt_type") {
      setPendingPlayerAwardAutofill(value === "player_award");
    }
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const buildSquadOptions = useCallback(
    (rows, fallbackTeamId) =>
      rows.map((row) => ({
        option_uid: row.player_id ? `player-${row.player_id}` : `manual-${manualIdRef.current++}`,
        label: row.player_name || row.label,
        player_id: row.player_id ? String(row.player_id) : "",
        player_name: row.player_name || row.label,
        team_id: row.team_id ? String(row.team_id) : String(fallbackTeamId || ""),
        metaText: stringifyMeta(row.meta),
        source: "squad",
      })),
    []
  );

  const applyPlayerAwardOptions = useCallback((keys = selectedPlayerKeys, rows = squadCandidates) => {
    const selected = rows.filter((row) => keys.includes(row.key));
    if (!selected.length) {
      setSquadError("Could not load squad data for this club.");
      return false;
    }
    setSquadError("");
    setForm((prev) => ({
      ...prev,
      options: buildSquadOptions(selected, prev.team_id_helper),
    }));
    return true;
  }, [buildSquadOptions, selectedPlayerKeys, squadCandidates]);

  const updateOption = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      options: prev.options.map((option, idx) => {
        if (idx !== index) return option;
        const next = { ...option, [key]: value };
        if (prev.prompt_type === "player_award" && key === "player_name" && !option.label) {
          next.label = value;
        }
        return next;
      }),
    }));
  };

  const addOption = () => {
    setForm((prev) => ({ ...prev, options: [...prev.options, makeManualOption()] }));
  };

  const removeOption = (index) => {
    setForm((prev) => ({
      ...prev,
      options: prev.options.filter((_, idx) => idx !== index),
    }));
  };

  const moveOption = (index, direction) => {
    setForm((prev) => {
      const next = [...prev.options];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      const temp = next[index];
      next[index] = next[target];
      next[target] = temp;
      return { ...prev, options: next };
    });
  };

  const applyPreset = (label) => {
    const preset = presetConfig[label];
    if (!preset) return;
    setForm((prev) => ({
      ...prev,
      prompt_type: preset.prompt_type,
      question: preset.question,
      duration_hours: preset.duration_hours,
      options: preset.options.map((option) => makeManualOption(option)),
    }));
    setTopError("");
    if (preset.prompt_type === "player_award") {
      if (squadCandidates.length) {
        const defaultKeys = selectedPlayerKeys.length
          ? selectedPlayerKeys
          : squadCandidates.slice(0, 4).map((row) => row.key);
        applyPlayerAwardOptions(defaultKeys, squadCandidates);
        setPendingPlayerAwardAutofill(false);
      } else {
        setPendingPlayerAwardAutofill(true);
      }
    } else {
      setPendingPlayerAwardAutofill(false);
    }
  };

  const selectTeam = (team) => {
    const nextTeamName = firstDefined(team?.name, team?.team_name, form.team_name);
    const resolvedLeague = resolveTeamLeagueContext(team, form);
    const nextSport = inferSportFromLeague(resolvedLeague.league_key, resolvedLeague.league_name, inferSport(team));
    setSelectedTeam({ ...team, query: teamQuery.trim() });
    setTeamQuery(nextTeamName);
    setTeamResults([]);
    setManagementFilters((prev) => ({
      ...prev,
      team_name: nextTeamName,
      sport: nextSport || prev.sport || "football",
      league_key: resolvedLeague.league_key || "",
    }));
    setPendingPlayerAwardAutofill(true);
    setSquadError("");
    setForm((prev) => ({
      ...prev,
      team_name: nextTeamName,
      sport: nextSport || prev.sport || "football",
      season: String(getDefaultFootballSeason()),
      league_id_helper: resolvedLeague.league_id ? String(resolvedLeague.league_id) : "",
      league_key: resolvedLeague.league_key || "",
      league_name: resolvedLeague.league_name || "",
      ...(prev.prompt_type === "player_award" ? { options: [makeManualOption(), makeManualOption()] } : {}),
      team_id_helper: String(
        firstDefined(team?.team_id, team?.internal_team_id, team?.id, team?.provider_team_id, prev.team_id_helper) || ""
      ),
    }));
  };

  const applySuggestedCompetition = () => {
    setForm((prev) => ({
      ...prev,
      league_id_helper: suggestedCompetition.league_id ? String(suggestedCompetition.league_id) : prev.league_id_helper,
      league_key: suggestedCompetition.league_key || "",
      league_name: suggestedCompetition.league_name || "",
    }));
  };

  const clearCompetitionFields = () => {
    setForm((prev) => ({
      ...prev,
      league_key: "",
      league_name: "",
    }));
  };

  const bulkSelectPlayers = (mode) => {
    if (mode === "clear") {
      setSelectedPlayerKeys([]);
      if (form.prompt_type === "player_award") {
        setForm((prev) => ({ ...prev, options: [makeManualOption(), makeManualOption()] }));
      }
      return;
    }
    const nextKeys =
      mode === "all"
        ? squadCandidates.map((row) => row.key)
        : squadCandidates.slice(0, mode === "top6" ? 6 : 4).map((row) => row.key);
    setSelectedPlayerKeys(nextKeys);
    if (form.prompt_type === "player_award") {
      applyPlayerAwardOptions(nextKeys, squadCandidates);
    }
  };

  const togglePlayer = (key) => {
    setSelectedPlayerKeys((prev) => {
      const next = prev.includes(key) ? prev.filter((value) => value !== key) : [...prev, key];
      if (form.prompt_type === "player_award") {
        applyPlayerAwardOptions(next, squadCandidates);
      }
      return next;
    });
  };

  const reloadSquad = () => {
    setPendingPlayerAwardAutofill(form.prompt_type === "player_award");
    setSquadReloadTick((prev) => prev + 1);
  };

  useEffect(() => {
    if (form.prompt_type !== "player_award") return;
    if (!pendingPlayerAwardAutofill) return;
    if (squadLoading) return;
    if (!squadCandidates.length) return;
    const defaultKeys = selectedPlayerKeys.length
      ? selectedPlayerKeys
      : squadCandidates.slice(0, 4).map((row) => row.key);
    const applied = applyPlayerAwardOptions(defaultKeys, squadCandidates);
    if (applied) setPendingPlayerAwardAutofill(false);
  }, [applyPlayerAwardOptions, form.prompt_type, pendingPlayerAwardAutofill, selectedPlayerKeys, squadCandidates, squadLoading]);

  useEffect(() => {
    if (!selectedPromptId) {
      setPromptDetail(null);
      setPromptDetailError("");
      setEditingPrompt(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setPromptDetailLoading(true);
      setPromptDetailError("");
      try {
        const data = await getAdminRoomPrompt(selectedPromptId);
        if (cancelled) return;
        const row = data?.prompt || data;
        setPromptDetail(row || null);
      } catch (err) {
        if (!cancelled) {
          setPromptDetail(null);
          setPromptDetailError(err?.response?.data?.detail || "Could not load prompt details.");
        }
      } finally {
        if (!cancelled) setPromptDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPromptId]);

  const buildPayload = () => ({
    team_name: safeTrim(form.team_name),
    sport: safeTrim(form.sport),
    prompt_type: form.prompt_type,
    question: safeTrim(form.question),
    duration_hours: Number(form.duration_hours),
    is_active: true,
    ...(safeNum(form.team_id_helper) ? { team_id: Number(form.team_id_helper) } : {}),
    ...(safeTrim(form.season) ? { season: Number(form.season) } : {}),
    ...(safeTrim(form.league_id_helper) ? { league_id: Number(form.league_id_helper) } : {}),
    ...(safeTrim(form.league_key) ? { league_key: safeTrim(form.league_key) } : {}),
    ...(safeTrim(form.league_name) ? { league_name: safeTrim(form.league_name) } : {}),
    options: (Array.isArray(form.options) ? form.options : [])
      .filter((option) => safeTrim(option?.label))
      .map((option) => {
        const meta = parseMetaText(option.metaText);
        return {
          label: safeTrim(option.label),
          ...(option.player_id ? { player_id: Number(option.player_id) } : {}),
          ...(safeTrim(option.player_name) ? { player_name: safeTrim(option.player_name) } : {}),
          ...(option.team_id ? { team_id: Number(option.team_id) } : {}),
          ...(meta && Object.keys(meta).length ? { meta } : {}),
        };
      }),
  });

  const buildEditPayload = (prompt = {}) => ({
    team_name: safeTrim(prompt.team_name),
    sport: safeTrim(prompt.sport),
    prompt_type: safeTrim(prompt.prompt_type),
    question: safeTrim(prompt.question),
    is_active: prompt.is_active !== false,
    duration_hours: Number(prompt.duration_hours || 168),
    ...(safeTrim(prompt.league_key) ? { league_key: safeTrim(prompt.league_key) } : {}),
    ...(safeTrim(prompt.league_name) ? { league_name: safeTrim(prompt.league_name) } : {}),
    options: (Array.isArray(prompt.options) ? prompt.options : [])
      .filter((option) => safeTrim(option?.label))
      .map((option) => {
        const meta = parseMetaText(option?.metaText);
        return {
          label: safeTrim(option.label),
          ...(option.player_id ? { player_id: Number(option.player_id) } : {}),
          ...(safeTrim(option.player_name) ? { player_name: safeTrim(option.player_name) } : {}),
          ...(option.team_id ? { team_id: Number(option.team_id) } : {}),
          ...(meta && Object.keys(meta).length ? { meta } : {}),
        };
      }),
  });

  const updateManagementFilter = (key, value) => {
    setManagementFilters((prev) => ({ ...prev, [key]: value }));
  };

  const openEditPrompt = () => {
    if (!promptDetail) return;
    const options = Array.isArray(promptDetail?.options) && promptDetail.options.length
      ? promptDetail.options.map(normalisePromptOption)
      : [makeManualOption(), makeManualOption()];
    setEditingPrompt({
      id: promptDetail?.id || promptDetail?.prompt_id,
      team_name: safeTrim(promptDetail?.team_name),
      sport: safeTrim(promptDetail?.sport),
      league_key: safeTrim(promptDetail?.league_key),
      league_name: safeTrim(promptDetail?.league_name),
      prompt_type: safeTrim(promptDetail?.prompt_type) || "player_award",
      question: safeTrim(promptDetail?.question),
      duration_hours: durationHoursFromPrompt(promptDetail),
      is_active: promptDetail?.is_active !== false,
      options,
    });
  };

  const updateEditingPromptField = (key, value) => {
    setEditingPrompt((prev) => ({ ...prev, [key]: value }));
  };

  const updateEditingPromptOption = (index, key, value) => {
    setEditingPrompt((prev) => ({
      ...prev,
      options: prev.options.map((option, idx) => (idx === index ? { ...option, [key]: value } : option)),
    }));
  };

  const addEditingPromptOption = () => {
    setEditingPrompt((prev) => ({ ...prev, options: [...prev.options, makeManualOption()] }));
  };

  const removeEditingPromptOption = (index) => {
    setEditingPrompt((prev) => ({ ...prev, options: prev.options.filter((_, idx) => idx !== index) }));
  };

  const moveEditingPromptOption = (index, direction) => {
    setEditingPrompt((prev) => {
      const next = [...prev.options];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      const temp = next[index];
      next[index] = next[target];
      next[target] = temp;
      return { ...prev, options: next };
    });
  };

  const refreshPromptList = () => {
    setPromptListReloadTick((prev) => prev + 1);
  };

  const clearForm = () => {
    setForm({
      ...emptyForm,
      season: String(getDefaultFootballSeason()),
      options: [makeManualOption(), makeManualOption()],
    });
    setTeamQuery("");
    setTeamResults([]);
    setSelectedTeam(null);
    setShowCompetitionFields(false);
    setSquadCandidates([]);
    setSelectedPlayerKeys([]);
    setSquadError("");
    setSquadReloadTick(0);
    setPendingPlayerAwardAutofill(true);
    setTopError("");
    setSuccess("");
    setResponseData(null);
  };

  const submit = async (evt) => {
    evt.preventDefault();
    if (!canPublish || submitting || submitLockRef.current) return;
    submitLockRef.current = true;
    setSubmitting(true);
    setTopError("");
    setSuccess("");
    try {
      const data = await createAdminRoomPrompt(buildPayload());
      setResponseData(data || null);
      setSuccess("Room question published.");
      refreshPromptList();
    } catch (err) {
      setTopError(err?.response?.data?.detail || err?.message || "Could not publish room question.");
    } finally {
      setSubmitting(false);
      submitLockRef.current = false;
    }
  };

  const savePromptEdit = async () => {
    if (!editingPrompt?.id || savingPrompt) return;
    setSavingPrompt(true);
    setPromptDetailError("");
    try {
      const data = await updateAdminRoomPrompt(editingPrompt.id, buildEditPayload(editingPrompt));
      const next = data?.prompt || data;
      setPromptDetail(next || promptDetail);
      setEditingPrompt(null);
      setSuccess("Prompt saved.");
      refreshPromptList();
    } catch (err) {
      setPromptDetailError(err?.response?.data?.detail || "Could not save prompt.");
    } finally {
      setSavingPrompt(false);
    }
  };

  const triggerPromptAction = (kind, prompt) => {
    setConfirmAction({ kind, prompt });
  };

  const runConfirmedPromptAction = async () => {
    if (!confirmAction?.prompt || confirmBusy) return;
    const promptId = confirmAction.prompt?.id || confirmAction.prompt?.prompt_id;
    if (!promptId) return;
    setConfirmBusy(true);
    try {
      if (confirmAction.kind === "deactivate") {
        await deactivateAdminRoomPrompt(promptId);
        setSuccess("Prompt deactivated.");
      } else if (confirmAction.kind === "reactivate") {
        await reactivateAdminRoomPrompt(promptId);
        setSuccess("Prompt reactivated.");
      } else if (confirmAction.kind === "delete") {
        await deleteAdminRoomPrompt(promptId);
        setSuccess("Prompt deleted.");
        if (selectedPromptId === promptId) {
          setSelectedPromptId(null);
        }
      }
      setConfirmAction(null);
      refreshPromptList();
    } catch (err) {
      setPromptDetailError(err?.response?.data?.detail || "Could not complete prompt action.");
    } finally {
      setConfirmBusy(false);
    }
  };

  const duplicatePromptIntoForm = async (prompt) => {
    const promptId = prompt?.id || prompt?.prompt_id;
    if (!promptId) return;
    try {
      const data = await getAdminRoomPrompt(promptId);
      loadPromptIntoCreateForm(data?.prompt || data || prompt);
      setSuccess("Prompt loaded into create form.");
    } catch (err) {
      setTopError(err?.response?.data?.detail || "Could not duplicate prompt into form.");
    }
  };

  return (
    <>
    <div style={{ display: "grid", gap: 14 }}>
      {success ? (
        <div
          style={{
            position: "sticky",
            top: 12,
            zIndex: 5,
            border: "1px solid rgba(34,197,94,0.45)",
            background: "rgba(21,128,61,0.2)",
            color: "#dcfce7",
            borderRadius: 12,
            padding: "10px 12px",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div>
            <strong>{success}</strong>
            {responseData ? (
              <span style={{ marginLeft: 8, color: "#bbf7d0" }}>
                Prompt ID {responseData?.id || responseData?.prompt_id || "—"}
              </span>
            ) : null}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={() => setSuccess("")}
              style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer" }}
            >
              Duplicate and edit
            </button>
            <button
              type="button"
              onClick={clearForm}
              style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer" }}
            >
              Clear form
            </button>
          </div>
        </div>
      ) : null}

      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "minmax(0, 1.18fr) minmax(320px, 0.82fr)" }}>
        <form onSubmit={submit} style={{ ...cardStyle, display: "grid", gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "1.05rem" }}>Room Questions</h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#94a3b8" }}>
              Guided publishing flow: choose a club, apply a preset, pull in squad candidates, and publish.
            </p>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {Object.keys(presetConfig).map((label) => (
              <button
                key={label}
                type="button"
                onClick={() => applyPreset(label)}
                style={{
                  borderRadius: 999,
                  border: "1px solid rgba(148,163,184,0.28)",
                  background: "rgba(255,255,255,0.05)",
                  color: "#e8f5ec",
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            <div style={labelStyle}>
              <span>Select club</span>
              <div style={{ position: "relative" }}>
                <input
                  value={teamQuery}
                  onChange={(e) => {
                    setTeamQuery(e.target.value);
                    setSelectedTeam(null);
                    updateField("team_name", e.target.value);
                  }}
                  placeholder="Search clubs..."
                  style={{
                    ...inputStyle,
                    borderColor: errors.team_name ? "rgba(248,113,113,0.6)" : DEFAULT_BORDER_COLOR,
                  }}
                />
                {teamSearchLoading ? (
                  <div style={{ position: "absolute", top: 12, right: 12, fontSize: 12, color: "#94a3b8" }}>
                    Searching...
                  </div>
                ) : null}
                {teamResults.length ? (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      left: 0,
                      right: 0,
                      border: "1px solid rgba(148,163,184,0.24)",
                      background: "rgba(2,6,23,0.98)",
                      borderRadius: 12,
                      overflow: "hidden",
                      zIndex: 10,
                    }}
                  >
                    {teamResults.map((team, index) => (
                      <div key={team?.team_id || team?.id || `${team?.name}-${index}`} style={{ borderTop: index ? "1px solid rgba(148,163,184,0.14)" : "none" }}>
                        <TeamSearchResult team={team} onSelect={selectTeam} />
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
              {errors.team_name ? <div style={errorStyle}>{errors.team_name}</div> : <div style={helperStyle}>Search once, then the rest of the room metadata fills itself in.</div>}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
              <label style={labelStyle}>
                <span>Sport</span>
                <input
                  value={form.sport}
                  onChange={(e) => updateField("sport", e.target.value)}
                  style={{
                    ...inputStyle,
                    borderColor: errors.sport ? "rgba(248,113,113,0.6)" : DEFAULT_BORDER_COLOR,
                  }}
                />
                {errors.sport ? <div style={errorStyle}>{errors.sport}</div> : <div style={helperStyle}>Auto-filled from club selection, still editable.</div>}
              </label>
              <label style={labelStyle}>
                <span>Prompt type</span>
                <select value={form.prompt_type} onChange={(e) => updateField("prompt_type", e.target.value)} style={inputStyle}>
                  {promptTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </label>
              <label style={labelStyle}>
                <span>Team ID helper</span>
                <input value={form.team_id_helper} onChange={(e) => updateField("team_id_helper", e.target.value)} style={inputStyle} />
              </label>
              <label style={labelStyle}>
                <span>Season</span>
                <input value={form.season} onChange={(e) => updateField("season", e.target.value)} style={inputStyle} />
                <div style={helperStyle}>Used for squad lookup and carried into the prompt context.</div>
              </label>
              <label style={labelStyle}>
                <span>Duration (hours)</span>
                <input
                  type="number"
                  min="1"
                  value={form.duration_hours}
                  onChange={(e) => updateField("duration_hours", e.target.value)}
                  style={{
                    ...inputStyle,
                    borderColor: errors.duration_hours ? "rgba(248,113,113,0.6)" : DEFAULT_BORDER_COLOR,
                  }}
                />
                {errors.duration_hours ? <div style={errorStyle}>{errors.duration_hours}</div> : <div style={helperStyle}>Defaults to 7 days for fast reuse.</div>}
              </label>
            </div>

            <div
              style={{
                border: "1px solid rgba(148,163,184,0.16)",
                borderRadius: 12,
                padding: 12,
                background: "rgba(255,255,255,0.02)",
                display: "grid",
                gap: 10,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <div style={{ color: "#f8fafc", fontWeight: 800 }}>Competition-specific</div>
                  <div style={helperStyle}>
                    Leave competition blank for club-wide questions that should appear across the team page.
                  </div>
                  {missingLeagueKeyWarning ? (
                    <div style={{ ...errorStyle, marginTop: 6 }}>
                      This prompt may display on the club page but will not currently be votable without a competition context.
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => setShowCompetitionFields((prev) => !prev)}
                  style={{
                    borderRadius: 999,
                    border: "1px solid rgba(148,163,184,0.24)",
                    background: "rgba(255,255,255,0.04)",
                    color: "#e8f5ec",
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {showCompetitionFields ? "Hide advanced" : "Add competition"}
                </button>
              </div>

              {showCompetitionFields ? (
                <>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={applySuggestedCompetition}
                      disabled={!safeTrim(suggestedCompetition.league_key) && !safeTrim(suggestedCompetition.league_name)}
                      style={{
                        ...inputStyle,
                        width: "auto",
                        padding: "8px 12px",
                        cursor: "pointer",
                        opacity: !safeTrim(suggestedCompetition.league_key) && !safeTrim(suggestedCompetition.league_name) ? 0.55 : 1,
                      }}
                    >
                      Use selected club competition
                    </button>
                    <button
                      type="button"
                      onClick={clearCompetitionFields}
                      style={{
                        ...inputStyle,
                        width: "auto",
                        padding: "8px 12px",
                        cursor: "pointer",
                      }}
                    >
                      Clear competition
                    </button>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
                    <label style={labelStyle}>
                      <span>League key</span>
                      <input
                        value={form.league_key}
                        onChange={(e) => updateField("league_key", e.target.value)}
                        placeholder={suggestedCompetition.league_key || "Optional"}
                        style={inputStyle}
                      />
                      <div style={helperStyle}>
                        {suggestedCompetition.league_key
                          ? `Suggested from club: ${suggestedCompetition.league_key}`
                          : "Optional. Only set this for competition-specific questions."}
                      </div>
                    </label>
                    <label style={labelStyle}>
                      <span>League name</span>
                      <input
                        value={form.league_name}
                        onChange={(e) => updateField("league_name", e.target.value)}
                        placeholder={suggestedCompetition.league_name || "Optional"}
                        style={inputStyle}
                      />
                      <div style={helperStyle}>
                        {suggestedCompetition.league_name
                          ? `Suggested from club: ${suggestedCompetition.league_name}`
                          : "Optional. Add when you want the question tied to a specific competition."}
                      </div>
                    </label>
                    <label style={labelStyle}>
                      <span>League ID helper</span>
                      <input
                        value={form.league_id_helper}
                        onChange={(e) => updateField("league_id_helper", e.target.value)}
                        placeholder={suggestedCompetition.league_id ? String(suggestedCompetition.league_id) : "Optional"}
                        style={inputStyle}
                      />
                      <div style={helperStyle}>Used for squad lookup when the club belongs to a known competition.</div>
                    </label>
                  </div>
                </>
              ) : null}
            </div>
          </div>

          <label style={labelStyle}>
            <span>Question</span>
            <textarea
              rows={3}
              value={form.question}
              onChange={(e) => updateField("question", e.target.value)}
              style={{
                ...inputStyle,
                resize: "vertical",
                borderColor: errors.question ? "rgba(248,113,113,0.6)" : DEFAULT_BORDER_COLOR,
              }}
            />
            {errors.question ? <div style={errorStyle}>{errors.question}</div> : <div style={helperStyle}>Presets set this automatically, but you can tune the wording before publish.</div>}
          </label>

          <div
            style={{
              border: "1px solid rgba(148,163,184,0.16)",
              borderRadius: 12,
              padding: 12,
              background: "rgba(255,255,255,0.03)",
              display: "grid",
              gap: 10,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#f8fafc" }}>Football squad picker</div>
                <div style={{ fontSize: 12, color: "#94a3b8" }}>
                  Player of the Year should now fill itself in: pick a club, apply the preset, trim the names you want, publish.
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" onClick={() => bulkSelectPlayers("top4")} disabled={!squadCandidates.length} style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer" }}>
                  Use top 4
                </button>
                <button type="button" onClick={() => bulkSelectPlayers("top6")} disabled={!squadCandidates.length} style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer" }}>
                  Use top 6
                </button>
                <button type="button" onClick={() => bulkSelectPlayers("all")} disabled={!squadCandidates.length} style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer" }}>
                  Select all
                </button>
                <button type="button" onClick={() => bulkSelectPlayers("clear")} disabled={!squadCandidates.length} style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer" }}>
                  Clear
                </button>
                <button
                  type="button"
                  onClick={reloadSquad}
                  disabled={squadLoading || !safeNum(form.team_id_helper)}
                  style={{
                    ...inputStyle,
                    width: "auto",
                    padding: "8px 12px",
                    cursor: "pointer",
                    borderColor: "rgba(34,197,94,0.55)",
                    background: "rgba(34,197,94,0.14)",
                    color: "#dcfce7",
                  }}
                >
                  {squadLoading ? "Loading squad..." : "Reload players"}
                </button>
              </div>
            </div>

            {normalise(form.sport) !== "football" && normalise(form.sport) !== "soccer" ? (
              <div style={helperStyle}>Squad generation is currently available for football clubs only.</div>
            ) : null}

            <div style={{ display: "grid", gap: 8, maxHeight: 360, overflowY: "auto" }}>
              {squadError ? (
                <div style={{ color: "#fca5a5", fontSize: 13 }}>
                  {squadError}
                </div>
              ) : null}
              {!squadLoading && !squadCandidates.length ? (
                <div style={{ color: "#94a3b8", fontSize: 13 }}>
                  {safeNum(form.team_id_helper)
                    ? "No squad candidates are ready for this club yet."
                    : "Select a football club with a team ID to load top-rated squad options."}
                </div>
              ) : null}
              {squadCandidates.map((player) => {
                const selected = selectedPlayerKeys.includes(player.key);
                return (
                  <label
                    key={player.key}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 42px minmax(0, 1fr)",
                      gap: 10,
                      alignItems: "center",
                      border: selected ? "1px solid rgba(34,197,94,0.45)" : "1px solid rgba(148,163,184,0.14)",
                      background: selected ? "rgba(34,197,94,0.08)" : "rgba(2,6,23,0.34)",
                      borderRadius: 12,
                      padding: "10px 12px",
                      cursor: "pointer",
                    }}
                  >
                    <input type="checkbox" checked={selected} onChange={() => togglePlayer(player.key)} />
                    {player.meta?.photo ? (
                      <img src={player.meta.photo} alt="" style={{ width: 42, height: 42, borderRadius: 999, objectFit: "cover" }} />
                    ) : (
                      <div style={{ width: 42, height: 42, borderRadius: 999, display: "grid", placeItems: "center", background: "rgba(255,255,255,0.08)", color: "#f8fafc", fontWeight: 900 }}>
                        {(player.player_name || "?").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: "#f8fafc", fontWeight: 800 }}>{player.player_name}</div>
                      <div style={{ color: "#94a3b8", fontSize: 12 }}>
                        {player.meta?.position || "—"} • Avg rating {player.meta?.rating != null ? Number(player.meta.rating).toFixed(2) : "—"} • {player.meta?.apps ?? "—"} apps
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#f8fafc" }}>Answer options</div>
                <div style={{ ...helperStyle, marginTop: 2 }}>Edit, reorder, or remove before you publish.</div>
              </div>
              <button type="button" onClick={addOption} style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer" }}>
                Add option
              </button>
            </div>

            {form.options.map((option, index) => (
              <OptionCard
                key={option.option_uid || `option-${index}`}
                option={option}
                index={index}
                total={form.options.length}
                onChange={updateOption}
                onRemove={removeOption}
                onMove={moveOption}
              />
            ))}
            {errors.options ? <div style={errorStyle}>{errors.options}</div> : null}
            {errors.meta ? <div style={errorStyle}>{errors.meta}</div> : null}
          </div>

          {topError ? <div style={{ color: "#fca5a5", fontSize: 13 }}>{topError}</div> : null}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button type="button" onClick={clearForm} style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer" }}>
              Reset
            </button>
            <button
              type="submit"
              disabled={!canPublish}
              style={{
                ...inputStyle,
                width: "auto",
                padding: "8px 14px",
                cursor: canPublish ? "pointer" : "not-allowed",
                opacity: canPublish ? 1 : 0.7,
                borderColor: canPublish ? "rgba(34,197,94,0.55)" : "rgba(148,163,184,0.2)",
                background: canPublish ? "rgba(34,197,94,0.18)" : "rgba(255,255,255,0.03)",
                color: canPublish ? "#dcfce7" : "#94a3b8",
                fontWeight: 800,
              }}
            >
              {submitting ? "Publishing..." : "Publish question"}
            </button>
          </div>
        </form>

        <div style={{ ...cardStyle, display: "grid", gap: 12, alignContent: "start" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "1.05rem" }}>Live Preview</h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#94a3b8" }}>
              Styled to feel like the in-app room question card instead of a raw JSON preview.
            </p>
          </div>

          <div
            style={{
              border: "1px solid rgba(148,163,184,0.16)",
              borderRadius: 14,
              padding: 16,
              background: "linear-gradient(180deg, rgba(15,23,42,0.92), rgba(2,6,23,0.94))",
              display: "grid",
              gap: 14,
              boxShadow: "0 18px 38px rgba(0,0,0,0.26)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.7 }}>
                  {form.prompt_type.replace("_", " ")} • {form.team_name || "Team room"}
                </div>
                <div style={{ marginTop: 6, fontSize: 21, lineHeight: 1.18, fontWeight: 900, color: "#f8fafc" }}>
                  {form.question || "Your room question will preview here"}
                </div>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#bbf7d0",
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                  border: "1px solid rgba(34,197,94,0.32)",
                  background: "rgba(34,197,94,0.12)",
                  borderRadius: 999,
                  padding: "4px 8px",
                }}
              >
                {formatDuration(form.duration_hours) || "Set duration"}
              </div>
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              {previewOptions.filter((option) => safeTrim(option?.label)).length ? (
                previewOptions
                  .filter((option) => safeTrim(option?.label))
                  .map((option, index) => {
                    const meta = option.meta && typeof option.meta === "object" ? option.meta : {};
                    const photo = meta.photo || null;
                    const percent = previewPercents[index] || 0;
                    return (
                      <div
                        key={`preview-${index}`}
                        style={{
                          border: "1px solid rgba(148,163,184,0.16)",
                          borderRadius: 12,
                          padding: 12,
                          background: "rgba(255,255,255,0.04)",
                          display: "grid",
                          gap: 8,
                        }}
                      >
                        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                          {photo ? (
                            <img src={photo} alt="" style={{ width: 44, height: 44, borderRadius: 999, objectFit: "cover", flex: "0 0 auto" }} />
                          ) : (
                            <div style={{ width: 44, height: 44, borderRadius: 999, display: "grid", placeItems: "center", background: "rgba(255,255,255,0.08)", color: "#f8fafc", fontWeight: 900, flex: "0 0 auto" }}>
                              {(option.label || "?").slice(0, 1).toUpperCase()}
                            </div>
                          )}
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 15, fontWeight: 800, color: "#f8fafc" }}>{option.label}</div>
                            <div style={{ fontSize: 12, color: "#94a3b8" }}>
                              {meta.rating != null || meta.apps != null
                                ? `Avg rating ${meta.rating != null ? Number(meta.rating).toFixed(1) : "—"} - ${meta.apps ?? "—"} apps`
                                : meta.position
                                ? `Position ${meta.position}`
                                : "Option preview"}
                            </div>
                          </div>
                          <div style={{ fontSize: 13, color: "#f8fafc", fontWeight: 800 }}>{percent}%</div>
                        </div>
                        <div style={{ height: 8, borderRadius: 999, overflow: "hidden", background: "rgba(255,255,255,0.08)" }}>
                          <div style={{ width: `${percent}%`, height: "100%", background: "linear-gradient(90deg, #22c55e, #86efac)" }} />
                        </div>
                      </div>
                    );
                  })
              ) : (
                <div
                  style={{
                    border: "1px dashed rgba(148,163,184,0.26)",
                    borderRadius: 12,
                    padding: 18,
                    color: "#94a3b8",
                    fontSize: 13,
                    background: "rgba(255,255,255,0.03)",
                  }}
                >
                  Pick a club, apply a preset, and add options to see the finished card here.
                </div>
              )}
            </div>

            {responseData ? (
              <div
                style={{
                  border: "1px solid rgba(148,163,184,0.16)",
                  borderRadius: 12,
                  padding: 12,
                  background: "rgba(255,255,255,0.03)",
                  display: "grid",
                  gap: 6,
                }}
              >
                <div style={{ color: "#f8fafc", fontWeight: 800 }}>Created prompt summary</div>
                <div style={{ color: "#cbd5e1", fontSize: 13 }}>Prompt ID: {responseData?.id || responseData?.prompt_id || "—"}</div>
              <div style={{ color: "#94a3b8", fontSize: 12 }}>
                  {responseData?.team_name || form.team_name} • {responseData?.prompt_type || form.prompt_type}
                </div>
              </div>
            ) : null}

            <div
              style={{
                borderTop: "1px solid rgba(148,163,184,0.14)",
                paddingTop: 12,
                display: "grid",
                gap: 10,
              }}
            >
              <div>
                <div style={{ color: "#f8fafc", fontWeight: 800 }}>Manage Room Questions</div>
                <div style={helperStyle}>Search, inspect, edit, deactivate, reactivate, duplicate, and delete team room questions.</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 8 }}>
                <input value={managementFilters.team_name} onChange={(e) => updateManagementFilter("team_name", e.target.value)} placeholder="Team name" style={inputStyle} />
                <input value={managementFilters.sport} onChange={(e) => updateManagementFilter("sport", e.target.value)} placeholder="Sport" style={inputStyle} />
                <select value={managementFilters.prompt_type} onChange={(e) => updateManagementFilter("prompt_type", e.target.value)} style={inputStyle}>
                  <option value="">All prompt types</option>
                  {promptTypes.map((type) => (
                    <option key={`filter-${type}`} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <input value={managementFilters.league_key} onChange={(e) => updateManagementFilter("league_key", e.target.value)} placeholder="League key" style={inputStyle} />
                <select value={managementFilters.active_only} onChange={(e) => updateManagementFilter("active_only", e.target.value)} style={inputStyle}>
                  <option value="false">All statuses</option>
                  <option value="true">Active only</option>
                </select>
              </div>
              {promptListLoading ? <div style={helperStyle}>Loading prompts...</div> : null}
              {promptListError ? <div style={errorStyle}>{promptListError}</div> : null}
              {!promptListLoading && !promptListError && !existingPrompts.length ? (
                <div style={helperStyle}>No prompts found for this club yet.</div>
              ) : null}
              <div style={{ display: "grid", gap: 8, maxHeight: 320, overflowY: "auto" }}>
                {existingPrompts.map((prompt, index) => {
                  const options = Array.isArray(prompt?.options) ? prompt.options : [];
                  return (
                    <div
                      key={prompt?.id || prompt?.prompt_id || `prompt-${index}`}
                      style={{
                        border: "1px solid rgba(148,163,184,0.14)",
                        borderRadius: 12,
                        padding: 12,
                        background: "rgba(255,255,255,0.03)",
                        display: "grid",
                        gap: 8,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                        <div style={{ color: "#f8fafc", fontWeight: 800 }}>{prompt?.question || "Untitled prompt"}</div>
                        <PromptStatusBadge active={prompt?.is_active} />
                      </div>
                      <div style={{ color: "#94a3b8", fontSize: 12 }}>
                        {String(prompt?.prompt_type || "prompt").replace("_", " ")} • Prompt ID {prompt?.id || prompt?.prompt_id || "—"} • {formatPromptScope(prompt)}
                      </div>
                      <div style={{ color: "#cbd5e1", fontSize: 12 }}>
                        {prompt?.team_name || "—"} • {prompt?.sport || "—"} • Created {formatTimestamp(prompt?.created_at)} • Expires {formatTimestamp(prompt?.expires_at)} • Votes {prompt?.total_votes ?? prompt?.votes_total ?? 0} • League {prompt?.league_key || "—"} • {options.length || prompt?.option_count || 0} options
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button type="button" onClick={() => setSelectedPromptId(prompt?.id || prompt?.prompt_id)} style={{ ...inputStyle, width: "auto", padding: "6px 10px", cursor: "pointer" }}>
                          View
                        </button>
                        <button type="button" onClick={async () => {
                          setSelectedPromptId(prompt?.id || prompt?.prompt_id);
                          const data = await getAdminRoomPrompt(prompt?.id || prompt?.prompt_id).catch(() => null);
                          const row = data?.prompt || data || prompt;
                          setPromptDetail(row);
                          const optionsForEdit = Array.isArray(row?.options) && row.options.length ? row.options.map(normalisePromptOption) : [makeManualOption(), makeManualOption()];
                          setEditingPrompt({
                            id: row?.id || row?.prompt_id,
                            team_name: safeTrim(row?.team_name),
                            sport: safeTrim(row?.sport),
                            league_key: safeTrim(row?.league_key),
                            league_name: safeTrim(row?.league_name),
                            prompt_type: safeTrim(row?.prompt_type) || "player_award",
                            question: safeTrim(row?.question),
                            duration_hours: durationHoursFromPrompt(row),
                            is_active: row?.is_active !== false,
                            options: optionsForEdit,
                          });
                        }} style={{ ...inputStyle, width: "auto", padding: "6px 10px", cursor: "pointer" }}>
                          Edit
                        </button>
                        <button type="button" onClick={() => duplicatePromptIntoForm(prompt)} style={{ ...inputStyle, width: "auto", padding: "6px 10px", cursor: "pointer" }}>
                          Duplicate
                        </button>
                        {prompt?.is_active === false ? (
                          <button type="button" onClick={() => triggerPromptAction("reactivate", prompt)} style={{ ...inputStyle, width: "auto", padding: "6px 10px", cursor: "pointer", color: "#bbf7d0" }}>
                            Reactivate
                          </button>
                        ) : (
                          <button type="button" onClick={() => triggerPromptAction("deactivate", prompt)} style={{ ...inputStyle, width: "auto", padding: "6px 10px", cursor: "pointer", color: "#fecaca" }}>
                            Deactivate
                          </button>
                        )}
                        <button type="button" onClick={() => triggerPromptAction("delete", prompt)} style={{ ...inputStyle, width: "auto", padding: "6px 10px", cursor: "pointer", color: "#fca5a5" }}>
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    {selectedPromptId ? (
      <div style={modalBackdropStyle}>
        <div style={modalCardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
            <div>
              <div style={{ color: "#f8fafc", fontWeight: 900, fontSize: 20 }}>Prompt details</div>
              <div style={helperStyle}>Inspect the full question, options, votes, and scope.</div>
            </div>
            <button type="button" onClick={() => { setSelectedPromptId(null); setEditingPrompt(null); }} style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer" }}>
              Close
            </button>
          </div>
          {promptDetailLoading ? <div style={helperStyle}>Loading prompt...</div> : null}
          {promptDetailError ? <div style={errorStyle}>{promptDetailError}</div> : null}
          {promptDetail && !editingPrompt ? (
            <div style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                <div>
                  <div style={{ color: "#f8fafc", fontWeight: 800, fontSize: 18 }}>{promptDetail.question || "Untitled prompt"}</div>
                  <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 4 }}>
                    {String(promptDetail.prompt_type || "prompt").replace("_", " ")} • Prompt ID {promptDetail.id || promptDetail.prompt_id || "—"}
                  </div>
                </div>
                <PromptStatusBadge active={promptDetail.is_active} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, color: "#cbd5e1", fontSize: 13 }}>
                <div>Team: {promptDetail.team_name || "—"}</div>
                <div>Sport: {promptDetail.sport || "—"}</div>
                <div>League key: {promptDetail.league_key || "—"}</div>
                <div>League name: {promptDetail.league_name || "—"}</div>
                <div>Created: {formatTimestamp(promptDetail.created_at)}</div>
                <div>Expires: {formatTimestamp(promptDetail.expires_at)}</div>
                <div>Votes: {promptDetail.total_votes ?? promptDetail.votes_total ?? 0}</div>
                <div>{formatPromptScope(promptDetail)}</div>
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                {(Array.isArray(promptDetail.options) ? promptDetail.options : []).map((option, index) => {
                  const meta = option?.meta && typeof option.meta === "object" ? option.meta : {};
                  const votes = option?.vote_count ?? option?.votes ?? 0;
                  const totalVotes = promptDetail.total_votes ?? promptDetail.votes_total ?? 0;
                  const pct = totalVotes ? Math.round((votes / totalVotes) * 100) : 0;
                  return (
                    <div key={option?.id || option?.player_id || `detail-option-${index}`} style={{ border: "1px solid rgba(148,163,184,0.14)", borderRadius: 12, padding: 12, background: "rgba(255,255,255,0.03)", display: "grid", gap: 6 }}>
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        {meta.photo ? <img src={meta.photo} alt="" style={{ width: 40, height: 40, borderRadius: 999, objectFit: "cover" }} /> : null}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ color: "#f8fafc", fontWeight: 800 }}>{option?.label || `Option ${index + 1}`}</div>
                          <div style={{ color: "#94a3b8", fontSize: 12 }}>
                            Option ID {option?.id || "—"} • Player {option?.player_name || "—"} • Player ID {option?.player_id || "—"}
                          </div>
                          <div style={{ color: "#94a3b8", fontSize: 12 }}>
                            {meta.rating != null || meta.apps != null
                              ? `Avg rating ${meta.rating != null ? Number(meta.rating).toFixed(1) : "—"} - ${meta.apps ?? "—"} apps`
                              : meta.position || "No extra stats"}
                          </div>
                        </div>
                        <div style={{ color: "#f8fafc", fontWeight: 800 }}>{votes} votes{totalVotes ? ` • ${pct}%` : ""}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" onClick={openEditPrompt} style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer" }}>Edit</button>
                <button type="button" onClick={() => duplicatePromptIntoForm(promptDetail)} style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer" }}>Duplicate</button>
                {promptDetail.is_active === false ? (
                  <button type="button" onClick={() => triggerPromptAction("reactivate", promptDetail)} style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer", color: "#bbf7d0" }}>Reactivate</button>
                ) : (
                  <button type="button" onClick={() => triggerPromptAction("deactivate", promptDetail)} style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer", color: "#fecaca" }}>Deactivate</button>
                )}
                <button type="button" onClick={() => triggerPromptAction("delete", promptDetail)} style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer", color: "#fca5a5" }}>Delete</button>
              </div>
            </div>
          ) : null}
          {editingPrompt ? (
            <div style={{ display: "grid", gap: 12 }}>
              <div style={{ color: "#f8fafc", fontWeight: 800, fontSize: 18 }}>Edit prompt</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
                <label style={labelStyle}><span>Team name</span><input value={editingPrompt.team_name} onChange={(e) => updateEditingPromptField("team_name", e.target.value)} style={inputStyle} /></label>
                <label style={labelStyle}><span>Sport</span><input value={editingPrompt.sport} onChange={(e) => updateEditingPromptField("sport", e.target.value)} style={inputStyle} /></label>
                <label style={labelStyle}><span>League key</span><input value={editingPrompt.league_key} onChange={(e) => updateEditingPromptField("league_key", e.target.value)} style={inputStyle} /></label>
                <label style={labelStyle}><span>League name</span><input value={editingPrompt.league_name} onChange={(e) => updateEditingPromptField("league_name", e.target.value)} style={inputStyle} /></label>
                <label style={labelStyle}><span>Prompt type</span><select value={editingPrompt.prompt_type} onChange={(e) => updateEditingPromptField("prompt_type", e.target.value)} style={inputStyle}>{promptTypes.map((type) => <option key={`edit-${type}`} value={type}>{type}</option>)}</select></label>
                <label style={labelStyle}><span>Duration (hours)</span><input value={editingPrompt.duration_hours} onChange={(e) => updateEditingPromptField("duration_hours", e.target.value)} style={inputStyle} /></label>
              </div>
              <label style={labelStyle}><span>Question</span><textarea rows={3} value={editingPrompt.question} onChange={(e) => updateEditingPromptField("question", e.target.value)} style={{ ...inputStyle, resize: "vertical" }} /></label>
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                  <div style={{ color: "#f8fafc", fontWeight: 800 }}>Options</div>
                  <button type="button" onClick={addEditingPromptOption} style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer" }}>Add option</button>
                </div>
                {editingPrompt.options.map((option, index) => (
                  <OptionCard
                    key={option.option_uid || `edit-option-${index}`}
                    option={option}
                    index={index}
                    total={editingPrompt.options.length}
                    onChange={updateEditingPromptOption}
                    onRemove={removeEditingPromptOption}
                    onMove={moveEditingPromptOption}
                  />
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button type="button" onClick={() => setEditingPrompt(null)} style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: "pointer" }}>Cancel</button>
                <button type="button" onClick={savePromptEdit} disabled={savingPrompt} style={{ ...inputStyle, width: "auto", padding: "8px 12px", cursor: savingPrompt ? "not-allowed" : "pointer", color: "#dcfce7", borderColor: "rgba(34,197,94,0.32)", background: "rgba(34,197,94,0.14)" }}>
                  {savingPrompt ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    ) : null}
    <ConfirmPromptActionModal
      open={Boolean(confirmAction)}
      title={
        confirmAction?.kind === "delete"
          ? "Delete prompt?"
          : confirmAction?.kind === "deactivate"
          ? "Deactivate prompt?"
          : "Reactivate prompt?"
      }
      description={
        confirmAction?.kind === "delete"
          ? `This will permanently remove "${confirmAction?.prompt?.question || "this question"}" for ${confirmAction?.prompt?.team_name || "this club"} and its votes.`
          : confirmAction?.kind === "deactivate"
          ? `Deactivate "${confirmAction?.prompt?.question || "this question"}"?`
          : `Reactivate "${confirmAction?.prompt?.question || "this question"}"?`
      }
      confirmLabel={
        confirmAction?.kind === "delete"
          ? "Delete prompt"
          : confirmAction?.kind === "deactivate"
          ? "Deactivate"
          : "Reactivate"
      }
      confirmTone={confirmAction?.kind === "delete" ? "danger" : "success"}
      onCancel={() => setConfirmAction(null)}
      onConfirm={runConfirmedPromptAction}
      busy={confirmBusy}
    />
    </>
  );
}
